import classNames from 'classnames';
import React, { FC, ReactChild } from 'react';
import { LinkProps } from 'react-router-dom';

import noop from '@wetransfer/transfer/oneui/core/utils/noop';

import styles from './Button.module.scss';

export type ButtonVariants =
  | 'primary'
  | 'neutral'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  | 'inverted'
  | 'neutralOutline'
  | 'proButton';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  Partial<{
    children: ReactChild;
    variant?: ButtonVariants;
    small?: boolean;
    square?: boolean;
    stroke?: boolean;
    link?: boolean;
    label: string;
    component?: React.FC;
    disabled?: boolean;
    tabindex?: number;
    onClick?: () => void;
    className?: string;
    testId?: string;
    fullWidth?: boolean;
    as: string;
  }> &
  Partial<LinkProps>;

const Button: FC<ButtonProps> = ({
  children,
  variant = 'neutral',
  small = false,
  square = false,
  stroke = false,
  link = false,
  disabled = false,
  fullWidth = false,
  label,
  component,
  tabindex = 0,
  onClick = noop,
  className,
  testId = 'button',
  as = 'button',
  ...rest
}) => {
  const buttonClassnames = classNames(
    styles[`${variant}`],
    {
      [styles[`${variant}Small`]]: small,
      [styles[`${variant}Square`]]: square,
      [styles[`${variant}SquareStroke`]]: square && stroke,
      [styles.disabled]: disabled,
      [styles.link]: link,
      [styles.fullWidth]: fullWidth,
    },
    className
  );

  const Component = component || as;

  return (
    <Component
      disabled={disabled}
      tabIndex={tabindex}
      type="button"
      className={buttonClassnames}
      onClick={onClick}
      data-testid={testId}
      aria-disabled={disabled ? true : undefined}
      {...rest}
    >
      {label}
      {children}
    </Component>
  );
};

export default Button;
